import React, { useState, useEffect } from 'react';
import moment from 'moment';
import Select from 'react-select';
import AsyncSelect from 'react-select/async';
import { XMarkIcon } from '@heroicons/react/24/outline';
import ConfirmModal from './components/ConfirmModal';
import { API } from './api/api';

let _worktypesCache = null;
const _accountProjectsCache = new Map();

const formatDecimalHoursToHHMM = (decimalHours) => {
  const hours = Math.floor(decimalHours);
  const minutes = Math.round((decimalHours - hours) * 60);
  return `${hours}:${minutes.toString().padStart(2, '0')}`;
};

const parseHHMMToDecimalHours = (timeString) => {
  const [hours, minutes] = timeString.split(':').map(Number);
  return hours + (minutes / 60);
};

const parseFlexibleTimeToDecimalHours = (timeString) => {
  if (!timeString || !timeString.trim()) return 0;
  const trimmed = timeString.trim();
  if (trimmed.includes(',')) {
    const v = parseFloat(trimmed.replace(',', '.'));
    if (isNaN(v)) throw new Error('Invalid decimal format');
    return v;
  }
  if (trimmed.includes(':')) return parseHHMMToDecimalHours(trimmed);
  const v = parseFloat(trimmed);
  if (isNaN(v)) throw new Error('Invalid number format');
  return v;
};

export default function EventFormModal({
  isOpen, onClose, onSave, start, end, initialData, onDelete,
  currentView, modalPosition, onTimeChange, setModalPosition,
  getModalPositionStyle, onNavigate, currentDate,
}) {
  const [date, setDate] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [hours, setHours] = useState('');
  const [lastChangedField, setLastChangedField] = useState(null);
  const [projectId, setProjectId] = useState('');
  const [projectName, setProjectName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountProjects, setAccountProjects] = useState([]);
  const [isLoadingAccountProjects, setIsLoadingAccountProjects] = useState(false);
  const [title, setTitle] = useState('');
  const [billable, setBillable] = useState(false);
  const [isWorkTime, setIsWorkTime] = useState(false);
  const [worktypeId, setWorktypeId] = useState('');
  const [worktypes, setWorktypes] = useState([]);
  const [errors, setErrors] = useState({});
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    if (isOpen && isMobile) {
      document.body.classList.add('modal-open');
      return () => document.body.classList.remove('modal-open');
    }
  }, [isOpen, isMobile]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch worktypes once per session
  useEffect(() => {
    if (_worktypesCache) { setWorktypes(_worktypesCache); return; }
    API.fetchWorktypes()
      .then(data => {
        if (data.success) { _worktypesCache = data.data; setWorktypes(data.data); }
      })
      .catch(() => {});
  }, []);

  // Account search (debounced)
  const accountDebounceRef = React.useRef(null);
  const loadAccountOptions = (inputValue, callback) => {
    clearTimeout(accountDebounceRef.current);
    const delay = inputValue ? 350 : 0;
    accountDebounceRef.current = setTimeout(async () => {
      try {
        const data = await API.searchAccounts(inputValue || '');
        callback(data.success ? data.data.map(a => ({ value: a.id, label: a.name })) : []);
      } catch {
        callback([]);
      }
    }, delay);
  };

  // Fetch projects when account changes, cached by accountId
  useEffect(() => {
    if (accountId) {
      if (_accountProjectsCache.has(accountId)) {
        setAccountProjects(_accountProjectsCache.get(accountId));
        if (!initialData || accountId !== initialData.accountId) { setProjectId(''); setProjectName(''); }
        return;
      }
      setIsLoadingAccountProjects(true);
      if (!initialData || accountId !== initialData.accountId) { setProjectId(''); setProjectName(''); }
      API.fetchAccountProjects(accountId)
        .then(data => {
          const projects = data.success ? data.data : [];
          _accountProjectsCache.set(accountId, projects);
          setAccountProjects(projects);
        })
        .catch(() => setAccountProjects([]))
        .finally(() => setIsLoadingAccountProjects(false));
    } else {
      setAccountProjects([]);
      setProjectId('');
      setProjectName('');
    }
  }, [accountId]);

  // Auto-fill title from project name
  useEffect(() => {
    if (projectId) {
      const found = accountProjects.find(p => p.id === projectId);
      if (found) {
        setProjectName(found.name);
        setTitle(found.name);
      }
    }
  }, [projectId, accountProjects]);

  // Auto-set billable from worktype
  useEffect(() => {
    const wt = worktypes.find(w => w.id === worktypeId);
    setBillable(wt ? !!wt.billable : false);
  }, [worktypeId, worktypes]);

  const resetForm = () => {
    setDate('');
    setStartTime('');
    setEndTime('');
    setHours('');
    setProjectId('');
    setProjectName('');
    setAccountId('');
    setAccountName('');
    setAccountProjects([]);
    setTitle('');
    setBillable(false);
    setIsWorkTime(false);
    setWorktypeId('');
    setErrors({});
    setHasSubmitted(false);
    setHasChanges(false);
  };

  // Populate form from initialData or slot selection
  useEffect(() => {
    if (initialData) {
      setDate(moment(initialData.start).format('YYYY-MM-DD'));
      setStartTime(moment(initialData.start).format('HH:mm'));
      setEndTime(moment(initialData.end).format('HH:mm'));
      const durationHours = moment(initialData.end).diff(moment(initialData.start)) / 3600000;
      setHours(formatDecimalHoursToHHMM(durationHours));
      setAccountId(initialData.accountId || '');
      setAccountName(initialData.accountName || '');
      setProjectId(initialData.projectId || '');
      setProjectName(initialData.projectName || '');
      setTitle(initialData.notes || initialData.title || '');
      setBillable(!!initialData.billable);
      setIsWorkTime(!!initialData.isWorkTime);
      setWorktypeId(initialData.worktypeId || '');
    } else if (start && end) {
      setDate(moment(start).format('YYYY-MM-DD'));
      setStartTime(moment(start).format('HH:mm'));
      setEndTime(moment(end).format('HH:mm'));
      const durationHours = moment(end).diff(moment(start)) / 3600000;
      setHours(formatDecimalHoursToHHMM(durationHours));
    }
  }, [start, end, initialData]);

  // Sync startTime/endTime -> hours
  useEffect(() => {
    if (lastChangedField === 'startTime' || lastChangedField === 'endTime') {
      if (startTime && endTime && date) {
        const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const e = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (e.isAfter(s)) {
          setHours(formatDecimalHoursToHHMM(e.diff(s, 'minutes') / 60));
          if (onTimeChange) {
            onTimeChange({ ...(initialData || {}), start: s.toDate(), end: e.toDate(), logId: initialData?.logId || null });
          }
        }
      }
    }
  }, [startTime, endTime, date]);

  // Escape key
  useEffect(() => {
    const handleEscape = (e) => { if (e.key === 'Escape' && isOpen) handleClose(); };
    if (isOpen) document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, hasChanges]);

  // Arrow key navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleArrowKeys = (event) => {
      const tag = document.activeElement.tagName;
      const isEditable = tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || document.activeElement.isContentEditable;
      if (isEditable) return;

      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault();
        const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const e = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!s.isValid() || !e.isValid() || e.diff(s, 'minutes') <= 0) return;
        const delta = event.key === 'ArrowUp' ? -15 : 15;

        if (event.shiftKey) {
          const newEnd = e.clone().add(delta, 'minutes');
          if (newEnd.isSameOrBefore(s) || newEnd.diff(s, 'minutes') / 60 > 23) return;
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('endTime');
          if (onTimeChange) onTimeChange({ ...(initialData || {}), start: s.toDate(), end: newEnd.toDate(), logId: initialData?.logId || null });
        } else {
          const newStart = s.clone().add(delta, 'minutes');
          const newEnd = e.clone().add(delta, 'minutes');
          if (newStart.isBefore(moment(date).startOf('day')) || newEnd.isAfter(moment(date).endOf('day').add(1, 'minute'))) return;
          setStartTime(newStart.format('HH:mm'));
          setEndTime(newEnd.format('HH:mm'));
          setLastChangedField('startTime');
          if (onTimeChange) onTimeChange({ ...(initialData || {}), start: newStart.toDate(), end: newEnd.toDate(), logId: initialData?.logId || null });
        }
      }

      if ((event.key === 'ArrowLeft' || event.key === 'ArrowRight') && (currentView === 'week' || currentView === 'work_week' || currentView === 'three_day')) {
        event.preventDefault();
        const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
        const e = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
        if (!s.isValid() || !e.isValid()) return;
        const dayOfWeek = s.day();
        if (currentView === 'week') {
          if (event.key === 'ArrowLeft' && dayOfWeek === 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek === 0) return;
        }
        if (currentView === 'work_week') {
          if (event.key === 'ArrowLeft' && dayOfWeek <= 1) return;
          if (event.key === 'ArrowRight' && dayOfWeek >= 5) return;
        }
        const delta = event.key === 'ArrowLeft' ? -1 : 1;
        const newStart = s.clone().add(delta, 'days');
        const newEnd = e.clone().add(delta, 'days');
        if (currentView === 'work_week' && (newStart.day() === 0 || newStart.day() === 6)) return;

        setDate(newStart.format('YYYY-MM-DD'));
        setStartTime(newStart.format('HH:mm'));
        setEndTime(newEnd.format('HH:mm'));
        setLastChangedField('startTime');

        let shouldUpdateModalPosition = true;
        if (currentView === 'three_day' && onNavigate && currentDate) {
          const calStart = moment(currentDate).startOf('day');
          const calEnd = moment(calStart).add(2, 'days').endOf('day');
          if (newStart.isBefore(calStart, 'day') || newStart.isAfter(calEnd, 'day')) {
            onNavigate(newStart.toDate());
            shouldUpdateModalPosition = false;
            setTimeout(() => {
              setModalPosition({ right: '20px', position: 'fixed', top: '80px', zIndex: 2600 });
            }, 50);
          }
        }
        if (shouldUpdateModalPosition) setModalPosition(getModalPositionStyle(newStart.toDate(), currentView));
        if (onTimeChange) onTimeChange({ ...(initialData || {}), start: newStart.toDate(), end: newEnd.toDate(), logId: initialData?.logId || null });
      }
    };

    document.addEventListener('keydown', handleArrowKeys);
    return () => document.removeEventListener('keydown', handleArrowKeys);
  }, [isOpen, date, startTime, endTime, onTimeChange, initialData, start, end, currentView]);

  // Track changes
  useEffect(() => {
    setHasChanges(
      projectId !== (initialData?.projectId || '') ||
      title !== (initialData?.notes || initialData?.title || '') ||
      billable !== (!!initialData?.billable) ||
      isWorkTime !== (!!initialData?.isWorkTime) ||
      worktypeId !== (initialData?.worktypeId || '')
    );
  }, [projectId, title, billable, isWorkTime, worktypeId, initialData]);

  const validateForm = () => {
    const newErrors = {};
    if (!date) newErrors.date = 'Please select a date';
    if (!startTime) newErrors.startTime = 'Please enter a start time';
    if (!hours) newErrors.hours = 'Please enter hours';
    else {
      const hhmmPattern = /^\d{1,2}:\d{2}$/;
      const decimalPattern = /^\d*[,.]?\d+$/;
      if (!hhmmPattern.test(hours) && !decimalPattern.test(hours)) {
        newErrors.hours = 'Use format like 1:30, 0,1 or 1.5';
      } else {
        try {
          const n = parseFlexibleTimeToDecimalHours(hours);
          if (n <= 0) newErrors.hours = 'Hours must be greater than zero';
          if (n > 23) newErrors.hours = 'Hours must be less than 23';
        } catch {
          newErrors.hours = 'Invalid time format';
        }
      }
    }
    if (!isWorkTime) {
      if (!accountId) newErrors.accountId = 'Please select an account';
    }
    if (!worktypeId) newErrors.worktypeId = 'Please select a work type';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    setHasSubmitted(true);
    if (!validateForm()) return;

    const startDate = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm').toDate();
    let floatHours;
    try {
      floatHours = parseFlexibleTimeToDecimalHours(hours);
    } catch {
      setErrors(prev => ({ ...prev, hours: 'Invalid time format' }));
      return;
    }
    if (isNaN(floatHours) || floatHours <= 0) {
      setErrors(prev => ({ ...prev, hours: 'Invalid hours entered' }));
      return;
    }

    const endDate = new Date(startDate.getTime() + floatHours * 3600000);

    onSave({
      start: startDate,
      end: endDate,
      name: isWorkTime ? 'WorkTime' : title,
      projectId: isWorkTime ? '' : projectId,
      projectName: isWorkTime ? '' : projectName,
      accountId: isWorkTime ? '' : accountId,
      accountName: isWorkTime ? '' : accountName,
      billable: isWorkTime ? false : billable,
      isWorkTime,
      worktypeId,
      logId: initialData?.logId || null,
    });

    setHasSubmitted(false);
    resetForm();
    onClose();
  };

  const handleDelete = () => setShowDeleteConfirmModal(true);

  const handleConfirmDelete = () => {
    onDelete(initialData?.logId);
    resetForm();
    onClose();
    setShowDeleteConfirmModal(false);
  };

  const handleClose = () => {
    if (hasChanges) {
      setShowConfirmModal(true);
    } else {
      resetForm();
      onClose();
    }
  };

  const handleConfirmClose = () => {
    setShowConfirmModal(false);
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  const projectOptions = accountProjects.map(p => ({ value: p.id, label: p.name }));
  const lockedFields = !!initialData && !isWorkTime;

  const mobileStyles = {
    position: 'fixed', top: '20px', left: '50%', transform: 'translateX(-50%)',
    zIndex: 2600, bottom: '20px', display: 'flex', flexDirection: 'column',
  };
  const finalModalPosition = isMobile ? mobileStyles : modalPosition;

  const selectStyles = {
    control: (p) => ({ ...p, minHeight: '44px', padding: '2px 4px', fontSize: '16px' }),
    placeholder: (p) => ({ ...p, fontSize: '16px' }),
    input: (p) => ({ ...p, fontSize: '16px' }),
    singleValue: (p) => ({ ...p, fontSize: '16px' }),
  };

  return (
    <>
      <div
        className="fixed inset-0 bg-black bg-opacity-50 md:bg-transparent"
        style={{ zIndex: 2500 }}
        onClick={handleClose}
      />
      <div
        className="bg-white rounded-lg w-full max-w-[88vw] md:w-[400px] h-auto max-h-full md:max-h-[90vh] shadow-xl border border-gray-300 modal-mobile flex flex-col"
        style={finalModalPosition}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-3 p-3 md:p-6 pb-0 md:pb-0">
          <h2 className="text-xl font-semibold text-gray-900">
            {initialData ? 'Edit' : 'New entry'}
          </h2>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600 p-1 transition-colors" aria-label="Close">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-3 md:p-6 pt-0 md:pt-0">

          {/* Date + Hours */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Date:</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
              />
              {hasSubmitted && errors.date && <div className="text-red-500 text-sm mt-1">{errors.date}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Hours:</label>
              <input
                type="text"
                value={hours}
                onChange={(e) => {
                  setHours(e.target.value.replace(/[^0-9:,.]/g, ''));
                  setLastChangedField('hours');
                }}
                onBlur={() => {
                  try {
                    const parsed = parseFlexibleTimeToDecimalHours(hours);
                    if (!isNaN(parsed) && parsed > 0 && parsed <= 23) {
                      const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const newEnd = s.clone().add(parsed, 'hours');
                      setEndTime(newEnd.format('HH:mm'));
                      if (onTimeChange) onTimeChange({ ...(initialData || {}), start: s.toDate(), end: newEnd.toDate(), logId: initialData?.logId || null });
                    } else {
                      const s = moment(`${date} ${startTime}`, 'YYYY-MM-DD HH:mm');
                      const e = moment(`${date} ${endTime}`, 'YYYY-MM-DD HH:mm');
                      if (s.isValid() && e.isValid() && e.isAfter(s)) {
                        setHours(formatDecimalHoursToHHMM(e.diff(s, 'minutes') / 60));
                      } else setHours('');
                    }
                  } catch {
                    setHours('');
                  }
                }}
                className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
                placeholder="E.g. 1:30 or 0,1 or 1.5"
              />
              {hasSubmitted && errors.hours && <div className="text-red-500 text-sm mt-1">{errors.hours}</div>}
            </div>
          </div>

          {/* Start + End Time */}
          <div className="mb-2 grid grid-cols-2 md:flex md:flex-row gap-2 md:gap-4">
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">Start Time:</label>
              <input
                type="time"
                value={startTime}
                onChange={(e) => { setStartTime(e.target.value); setLastChangedField('startTime'); }}
                className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
              />
              {hasSubmitted && errors.startTime && <div className="text-red-500 text-sm mt-1">{errors.startTime}</div>}
            </div>
            <div className="flex flex-col md:w-1/2">
              <label className="text-sm md:text-base font-medium mb-1">End Time:</label>
              <input
                type="time"
                value={endTime}
                onChange={(e) => { setEndTime(e.target.value); setLastChangedField('endTime'); }}
                className="p-2 text-base rounded border border-gray-300 w-full touch-manipulation"
              />
            </div>
          </div>

          {/* WorkTime checkbox */}
          <div className="mb-3 flex items-center gap-2">
            <input
              type="checkbox"
              id="isWorkTime"
              checked={isWorkTime}
              onChange={(e) => {
                setIsWorkTime(e.target.checked);
                if (e.target.checked) {
                  setAccountId('');
                  setAccountName('');
                  setProjectId('');
                  setProjectName('');
                  setBillable(false);
                  setTitle('WorkTime');
                } else {
                  setTitle('');
                }
              }}
              disabled={!!initialData?.isWorkTime}
              className="w-4 h-4 cursor-pointer"
            />
            <label htmlFor="isWorkTime" className="text-sm md:text-base font-medium cursor-pointer select-none">
              WorkTime
            </label>
          </div>

          {/* Worktype */}
          <div className="mb-2 flex flex-col">
            <label className="text-sm md:text-base font-medium mb-1">Work Type:</label>
            <Select
              options={worktypes.map(w => ({ value: w.id, label: w.name }))}
              value={worktypeId ? { value: worktypeId, label: worktypes.find(w => w.id === worktypeId)?.name || worktypeId } : null}
              onChange={(opt) => setWorktypeId(opt?.value || '')}
              isClearable
              isDisabled={lockedFields}
              placeholder="Select a work type..."
              styles={selectStyles}
            />
            {hasSubmitted && errors.worktypeId && <div className="text-red-500 text-sm mt-1">{errors.worktypeId}</div>}
          </div>

          {!isWorkTime && (
            <>
              {/* Account */}
              <div className="mb-2 flex flex-col">
                <label className="text-sm md:text-base font-medium mb-1">Account:</label>
                <AsyncSelect
                  cacheOptions
                  defaultOptions
                  loadOptions={loadAccountOptions}
                  value={accountId ? { value: accountId, label: accountName } : null}
                  onChange={(opt) => {
                    setAccountId(opt?.value || '');
                    setAccountName(opt?.label || '');
                  }}
                  isClearable
                  isDisabled={lockedFields}
                  placeholder="Search for an account..."
                  noOptionsMessage={({ inputValue }) => inputValue ? 'No accounts found' : 'No accounts available'}
                  styles={selectStyles}
                />
                {hasSubmitted && errors.accountId && <div className="text-red-500 text-sm mt-1">{errors.accountId}</div>}
              </div>

              {/* Project */}
              <div className="mb-2 flex flex-col">
                <label className="text-sm md:text-base font-medium mb-1">Project:</label>
                <Select
                  options={projectOptions}
                  value={projectOptions.find(o => o.value === projectId) || null}
                  onChange={(opt) => setProjectId(opt?.value || '')}
                  isClearable
                  isDisabled={lockedFields || !accountId || isLoadingAccountProjects}
                  isLoading={isLoadingAccountProjects}
                  placeholder={
                    !accountId ? 'Select an account first...' :
                    isLoadingAccountProjects ? 'Loading projects...' :
                    'Select a project...'
                  }
                  styles={selectStyles}
                />
              </div>

              {/* Billable */}
              <div className="mb-3 flex items-center gap-2">
                <input
                  type="checkbox"
                  id="billable"
                  checked={billable}
                  disabled
                  className="w-4 h-4 cursor-not-allowed"
                />
                <label htmlFor="billable" className="text-sm md:text-base font-medium select-none">
                  Billable
                </label>
              </div>

              {/* Notes */}
              <div className="mb-2 flex flex-col">
                <label className="text-sm md:text-base font-medium mb-1">Notes:</label>
                <textarea
                  value={title}
                  onChange={(e) => setTitle(e.target.value.trimStart())}
                  className="p-2 text-base rounded border border-gray-300 resize-none touch-manipulation"
                  placeholder="Enter notes..."
                  maxLength={255}
                  rows={4}
                  style={{ fontSize: '16px' }}
                />
                <div className="text-sm text-gray-600 text-right">{title.length}/255</div>
              </div>
            </>
          )}
        </div>

        {/* Action buttons */}
        <div className="border-t border-gray-200 p-3 md:p-6 bg-white rounded-b-lg">
          <div className="flex flex-col md:flex-row justify-between gap-3 md:gap-0">
            <div className="order-2 md:order-1">
              {initialData && (
                <button
                  onClick={handleDelete}
                  className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-red-600 text-white rounded hover:bg-red-700 touch-manipulation font-medium"
                >
                  Delete
                </button>
              )}
            </div>
            <div className="order-1 md:order-2">
              <button
                onClick={handleSubmit}
                className="w-full md:w-auto px-6 py-3 md:px-4 md:py-2 bg-[#205799] text-white rounded hover:bg-[#273C61] touch-manipulation font-medium"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </div>

      <ConfirmModal
        isOpen={showConfirmModal}
        onConfirm={handleConfirmClose}
        onCancel={() => setShowConfirmModal(false)}
        title="Unsaved Changes"
        message="Are you sure you want to discard your changes?"
        confirmText="Discard"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
      <ConfirmModal
        isOpen={showDeleteConfirmModal}
        onConfirm={handleConfirmDelete}
        onCancel={() => setShowDeleteConfirmModal(false)}
        title="Delete Registration"
        message="Are you sure you want to delete this time registration?"
        confirmText="Delete"
        confirmButtonClass="bg-red-600 hover:bg-red-700"
      />
    </>
  );
}
